/**
 * The Brush paints and erases a Mask (see CONTEXT.md). Both variants are
 * Cornerstone `BrushTool` instances registered under their own names, like
 * upstream's segmentation mode does (`modes/segmentation/src/initToolGroups.ts`).
 */
export const BRUSH_TOOL_NAME = 'GleamerCircularBrush';
export const ERASER_TOOL_NAME = 'GleamerCircularEraser';
export const BRUSH_TOOL_NAMES = [BRUSH_TOOL_NAME, ERASER_TOOL_NAME];

// Cornerstone's brush size is a radius in world units (mm). BAC structures are
// 1–2 px wide on DBT (~0.1 mm pixel spacing), so the lower bound has to reach a
// single pixel.
export const MIN_BRUSH_SIZE = 0.1;
export const MAX_BRUSH_SIZE = 20;
export const DEFAULT_BRUSH_SIZE = 1;
// Multiplicative, so one step is as noticeable at 0.2 mm as at 10 mm.
const BRUSH_SIZE_STEP_FACTOR = 1.25;

// Outside a task there is no Observation, so everything is painted in one Segment.
export const DEFAULT_SEGMENT_KEY = 'default';

export const BRUSH_EVENTS = {
  SIZE_CHANGED: 'event::brush:sizeChanged',
};

/** The part of a Cornerstone tool group the brush reads. */
export type BrushToolGroup = {
  toolOptions?: Record<string, { mode?: string } | undefined>;
};

type PendingObservation = {
  code: string;
  status: string;
  index: number;
};

export function isBrushToolName(toolName?: string | null): boolean {
  return !!toolName && BRUSH_TOOL_NAMES.includes(toolName);
}

/** Returns the eraser for the brush and the brush for the eraser. */
export function getOtherBrushToolName(toolName: string): string {
  return toolName === ERASER_TOOL_NAME ? BRUSH_TOOL_NAME : ERASER_TOOL_NAME;
}

/**
 * A task spec only names the brush, but switching to the eraser activates a
 * tool the spec does not list. Callers that reset every task tool must reset
 * both variants, or the eraser stays active next to the brush.
 */
export function withBrushVariants(toolNames: string[]): string[] {
  if (!toolNames.some(isBrushToolName)) {
    return toolNames;
  }
  return Array.from(new Set([...toolNames, ...BRUSH_TOOL_NAMES]));
}

export function clampBrushSize(size: number): number {
  return Math.min(MAX_BRUSH_SIZE, Math.max(MIN_BRUSH_SIZE, size));
}

export function getNextBrushSize(
  size: number | undefined,
  direction: 'increase' | 'decrease'
): number {
  const current = clampBrushSize(size ?? DEFAULT_BRUSH_SIZE);
  const next =
    direction === 'increase'
      ? current * BRUSH_SIZE_STEP_FACTOR
      : current / BRUSH_SIZE_STEP_FACTOR;
  return clampBrushSize(Math.round(next * 100) / 100);
}

// The size slider is logarithmic: a linear 0.1–20 mm track leaves the
// pixel-sized brushes BAC needs in its first percent.
export const BRUSH_SLIDER_MAX = 100;
const LOG_SIZE_RANGE = Math.log(MAX_BRUSH_SIZE / MIN_BRUSH_SIZE);

export function brushSizeToSliderValue(size: number): number {
  return Math.round(
    (Math.log(clampBrushSize(size) / MIN_BRUSH_SIZE) / LOG_SIZE_RANGE) *
      BRUSH_SLIDER_MAX
  );
}

export function sliderValueToBrushSize(value: number): number {
  const ratio =
    Math.min(BRUSH_SLIDER_MAX, Math.max(0, value)) / BRUSH_SLIDER_MAX;
  const size = MIN_BRUSH_SIZE * Math.exp(ratio * LOG_SIZE_RANGE);
  return clampBrushSize(Math.round(size * 100) / 100);
}

/** The brush variant active in this tool group, if any. */
export function getActiveBrushToolName(
  toolGroup: BrushToolGroup | undefined | null
): string | null {
  return (
    BRUSH_TOOL_NAMES.find(
      toolName => toolGroup?.toolOptions?.[toolName]?.mode === 'Active'
    ) ?? null
  );
}

type HistoryMemo = { operationType?: string } | null | undefined;

/**
 * Cornerstone keeps one undo history for every tool: annotation edits are in
 * it too, and undoing one would desync the label pass. Brush strokes are the
 * entries made only of labelmap memos.
 */
export function isBrushStrokeHistoryItem(
  item: HistoryMemo | HistoryMemo[]
): boolean {
  const memos = Array.isArray(item) ? item : [item];
  return (
    memos.length > 0 && memos.every(memo => memo?.operationType === 'labelmap')
  );
}

/**
 * One Segment per Observation. The Observation may not exist yet (it is
 * created when it gets its first Region), so the key comes from the pending
 * observation the annotator selected.
 */
export function getSegmentKey({
  code,
  status,
  index,
}: PendingObservation): string {
  return `${code}:${status}:${index}`;
}

/** Same color rule as `addObservation` in the label pass slice. */
export function getObservationColor(
  colors: string[] | undefined | null,
  index: number
): string | undefined {
  return colors?.length ? colors[index % colors.length] : undefined;
}

/**
 * Tool group entries for both variants, added passive: a task activates the
 * Brush through its tool spec, the local viewer through the toolbar.
 */
export const brushTools = [
  { toolName: BRUSH_TOOL_NAME, activeStrategy: 'FILL_INSIDE_CIRCLE' },
  { toolName: ERASER_TOOL_NAME, activeStrategy: 'ERASE_INSIDE_CIRCLE' },
].map(({ toolName, activeStrategy }) => ({
  toolName,
  parentTool: 'Brush',
  configuration: {
    activeStrategy,
    brushSize: DEFAULT_BRUSH_SIZE,
    minRadius: MIN_BRUSH_SIZE,
    maxRadius: MAX_BRUSH_SIZE,
  },
}));
